import React from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import HourglassTopRoundedIcon from "@mui/icons-material/HourglassTopRounded";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";

const BRAND = { orange: "#F35A3A", dark: "#19242F" };
const DEFAULT_VISIBLE_ENTRIES = 5;

const ACTION_LABELS = {
  provision: "Creación del sitio",
  sync: "Sincronización",
  retry: "Reintento manual",
  social_preview_refresh: "Actualización de vista social",
};

const STATUS_LABELS = {
  live: "Publicado",
  created: "Creado",
  queued: "En cola",
  retrying: "Reintentando",
  provisioning: "Publicando",
  pending: "Pendiente",
  build_in_progress: "Compilando",
  update_in_progress: "Actualizando",
  pre_deploy_in_progress: "Preparando",
  completed: "Completado",
  success: "Completado",
  succeeded: "Completado",
  skipped: "Omitido",
  failed: "Fallido",
  build_failed: "Error de compilación",
  update_failed: "Error de actualización",
  pre_deploy_failed: "Error previo al despliegue",
  canceled: "Cancelado",
  deactivated: "Desactivado",
  unknown: "Sin estado",
};

const FAILED_STATUSES = new Set([
  "failed",
  "build_failed",
  "update_failed",
  "pre_deploy_failed",
  "canceled",
  "deactivated",
]);

const SUCCESS_STATUSES = new Set([
  "live",
  "completed",
  "success",
  "succeeded",
]);

const PENDING_STATUSES = new Set([
  "created",
  "queued",
  "retrying",
  "provisioning",
  "pending",
  "build_in_progress",
  "update_in_progress",
  "pre_deploy_in_progress",
]);

function clean(value) {
  return String(value ?? "").trim();
}

function humanize(value, fallback) {
  const normalized = clean(value);
  if (!normalized) return fallback;
  const words = normalized.replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function actionLabel(action) {
  const normalized = clean(action).toLowerCase();
  return ACTION_LABELS[normalized] || humanize(action, "Despliegue");
}

function statusMeta(status) {
  const normalized = clean(status).toLowerCase() || "unknown";
  const label = STATUS_LABELS[normalized] || humanize(status, "Sin estado");

  if (FAILED_STATUSES.has(normalized)) {
    return { label, color: "error", icon: <ErrorRoundedIcon fontSize="small" /> };
  }
  if (SUCCESS_STATUSES.has(normalized)) {
    return { label, color: "success", icon: <CheckCircleRoundedIcon fontSize="small" /> };
  }
  if (PENDING_STATUSES.has(normalized)) {
    return { label, color: "warning", icon: <HourglassTopRoundedIcon fontSize="small" /> };
  }
  return { label, color: "default", icon: <InfoRoundedIcon fontSize="small" /> };
}

function formatDate(value) {
  if (!value) return "Fecha no disponible";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Fecha no disponible";

  return date.toLocaleString("es-AR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatDuration(value) {
  const durationMs = Number(value);
  if (!Number.isFinite(durationMs) || durationMs < 0) return "Duración no disponible";
  if (durationMs < 1000) return `${Math.round(durationMs)} ms`;

  const totalSeconds = Math.round(durationMs / 1000);
  if (totalSeconds < 60) return `${totalSeconds} s`;

  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return seconds ? `${minutes} min ${seconds} s` : `${minutes} min`;
}

function normalizeHistory(project) {
  const history = project?.deployment?.history;
  if (!Array.isArray(history)) return [];

  return history
    .map((entry, index) => ({
      action: clean(entry?.action),
      status: clean(entry?.status),
      deployId: clean(entry?.deployId),
      date: entry?.date || null,
      durationMs: entry?.durationMs,
      error: clean(entry?.error),
      sourceIndex: index,
    }))
    .sort((left, right) => {
      const leftDate = left.date ? new Date(left.date).getTime() : Number.NaN;
      const rightDate = right.date ? new Date(right.date).getTime() : Number.NaN;
      const leftValid = Number.isFinite(leftDate);
      const rightValid = Number.isFinite(rightDate);

      if (leftValid && rightValid && leftDate !== rightDate) return rightDate - leftDate;
      if (leftValid !== rightValid) return rightValid ? 1 : -1;
      return right.sourceIndex - left.sourceIndex;
    });
}

function HistoryEntry({ entry, isLast }) {
  const meta = statusMeta(entry.status);

  return (
    <Box>
      <Stack direction="row" spacing={1.25} alignItems="flex-start">
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: 2.2,
            display: "grid",
            placeItems: "center",
            flexShrink: 0,
            color:
              meta.color === "error"
                ? "error.main"
                : meta.color === "success"
                  ? "success.main"
                  : meta.color === "warning"
                    ? "warning.dark"
                    : "rgba(25,36,47,.58)",
            bgcolor:
              meta.color === "error"
                ? "rgba(211,47,47,.08)"
                : meta.color === "success"
                  ? "rgba(1,142,66,.08)"
                  : meta.color === "warning"
                    ? "rgba(237,108,2,.09)"
                    : "rgba(25,36,47,.055)",
          }}
        >
          {meta.icon}
        </Box>

        <Stack spacing={0.8} sx={{ minWidth: 0, flex: 1 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={0.7}
            alignItems={{ sm: "center" }}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={0.7} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography sx={{ fontWeight: 900, color: BRAND.dark }}>
                {actionLabel(entry.action)}
              </Typography>
              <Chip
                size="small"
                label={meta.label}
                color={meta.color}
                variant={meta.color === "default" ? "outlined" : "filled"}
                sx={{ height: 24, fontWeight: 850 }}
              />
            </Stack>
            <Typography variant="caption" sx={{ color: "rgba(25,36,47,.55)", flexShrink: 0 }}>
              {formatDate(entry.date)}
            </Typography>
          </Stack>

          <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              variant="outlined"
              label={formatDuration(entry.durationMs)}
              sx={{ height: 24, fontWeight: 750, color: "rgba(25,36,47,.66)" }}
            />
            {entry.deployId ? (
              <Typography
                variant="caption"
                sx={{
                  alignSelf: "center",
                  color: "rgba(25,36,47,.62)",
                  fontFamily: "monospace",
                  overflowWrap: "anywhere",
                }}
              >
                Deploy: {entry.deployId}
              </Typography>
            ) : null}
          </Stack>

          {entry.error ? (
            <Box
              sx={{
                px: 1.2,
                py: 0.9,
                borderRadius: 2,
                bgcolor: "rgba(211,47,47,.055)",
                border: "1px solid rgba(211,47,47,.14)",
              }}
            >
              <Typography variant="caption" sx={{ color: "error.dark", overflowWrap: "anywhere" }}>
                {entry.error}
              </Typography>
            </Box>
          ) : null}
        </Stack>
      </Stack>

      {!isLast ? <Divider sx={{ my: 1.35, ml: 5.75 }} /> : null}
    </Box>
  );
}

export default function ProjectDeploymentHistory({
  project,
  initiallyVisible = DEFAULT_VISIBLE_ENTRIES,
}) {
  const [expanded, setExpanded] = React.useState(false);
  const entries = React.useMemo(() => normalizeHistory(project), [project]);
  const visibleLimit = Math.max(1, Number(initiallyVisible) || DEFAULT_VISIBLE_ENTRIES);
  const visibleEntries = expanded ? entries : entries.slice(0, visibleLimit);
  const hiddenCount = Math.max(0, entries.length - visibleLimit);

  React.useEffect(() => {
    if (entries.length <= visibleLimit) setExpanded(false);
  }, [entries.length, visibleLimit]);

  return (
    <Paper
      elevation={0}
      variant="outlined"
      sx={{
        p: { xs: 1.6, sm: 2 },
        borderRadius: 3,
        borderColor: "rgba(25,36,47,.10)",
        bgcolor: "rgba(25,36,47,.018)",
      }}
    >
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1.1} alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={1.1} alignItems="center" sx={{ minWidth: 0 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 2.4,
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
                color: BRAND.orange,
                bgcolor: "rgba(243,90,58,.10)",
              }}
            >
              <HistoryRoundedIcon fontSize="small" />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>
                Historial de despliegues
              </Typography>
              <Typography variant="caption" sx={{ color: "rgba(25,36,47,.58)" }}>
                Últimos estados registrados para esta página pública.
              </Typography>
            </Box>
          </Stack>
          <Chip
            size="small"
            label={`${entries.length} ${entries.length === 1 ? "evento" : "eventos"}`}
            variant="outlined"
            sx={{ flexShrink: 0, fontWeight: 850 }}
          />
        </Stack>

        {entries.length ? (
          <Stack spacing={0}>
            {visibleEntries.map((entry, index) => (
              <HistoryEntry
                key={`${entry.date || "sin-fecha"}-${entry.deployId || "sin-deploy"}-${entry.action || "deployment"}-${entry.sourceIndex}`}
                entry={entry}
                isLast={index === visibleEntries.length - 1}
              />
            ))}
          </Stack>
        ) : (
          <Box
            sx={{
              px: 1.4,
              py: 1.25,
              borderRadius: 2.4,
              bgcolor: "rgba(25,36,47,.035)",
            }}
          >
            <Typography variant="body2" sx={{ color: "rgba(25,36,47,.62)" }}>
              Todavía no hay despliegues registrados para este proyecto.
            </Typography>
          </Box>
        )}

        {hiddenCount > 0 ? (
          <Button
            type="button"
            size="small"
            onClick={() => setExpanded((current) => !current)}
            endIcon={expanded ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
            aria-expanded={expanded}
            sx={{
              alignSelf: "flex-start",
              p: 0,
              minWidth: 0,
              color: BRAND.orange,
              textTransform: "none",
              fontWeight: 900,
            }}
          >
            {expanded ? "Mostrar menos" : `Ver ${hiddenCount} eventos anteriores`}
          </Button>
        ) : null}
      </Stack>
    </Paper>
  );
}
